import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Sparkles, Phone, ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils';
import type { Scenario } from './ScenarioSummary';
const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
interface Message {
  id: string;
  type: 'bot' | 'user';
  text: string;
  quickReplies?: string[];
  inputType?: 'text' | 'email' | 'phone' | 'number' | 'currency' | 'select';
  placeholder?: string;
  selectOptions?: string[];
}
interface Step {
  field: keyof Scenario | null;
  ask: string;
  quickReplies?: string[];
  inputType?: Message['inputType'];
  placeholder?: string;
  selectOptions?: string[];
  parse?: (raw: string) => any;
}
interface ChatPanelProps {
  intent: 'purchase' | 'refinance' | 'equity';
  scenario: Scenario;
  onUpdate: (patch: Partial<Scenario>) => void;
}
function parseCurrency(raw: string): number {
  const n = parseFloat(raw.replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
}
function parseRate(r: string, fallbackMap?: Record<string, number>): number | undefined {
  if (fallbackMap && fallbackMap[r] != null) return fallbackMap[r];
  const n = parseFloat(r.replace(/[^0-9.]/g, ''));
  return isNaN(n) ? undefined : n;
}
const TARGET_RATE_STEP: Step = {
  field: 'targetRate',
  ask: 'What target interest rate are you hoping for? Type your exact target rate for the most accurate estimate.',
  inputType: 'number',
  placeholder: 'e.g. 5.875',
  parse: r => parseRate(r)
};
const CURRENT_RATE_STEP: Step = {
  field: 'currentRate',
  ask: "What's your current interest rate? Type your exact rate for the most accurate savings estimate.",
  inputType: 'number',
  placeholder: 'e.g. 6.875',
  parse: r => parseRate(r)
};
const NAME_STEP: Step = {
  field: 'name',
  ask: "Almost done! What's your full name?",
  inputType: 'text',
  placeholder: 'Full name'
};
const EMAIL_STEP: Step = {
  field: 'email',
  ask: null as any,
  inputType: 'email',
  placeholder: 'you@example.com'
};
const PHONE_STEP: Step = {
  field: 'phone',
  ask: 'Want results even faster? 🚀 Drop your number below and a Loan Pronto advisor will text you with answers within minutes — way quicker than waiting on email.',
  inputType: 'text',
  placeholder: '(555) 123-4567'
};
function getSteps(intent: 'purchase' | 'refinance' | 'equity'): Step[] {
  if (intent === 'purchase') {
    return [TARGET_RATE_STEP, {
      field: 'budget',
      ask: "Awesome, let's find your fit. What's your target purchase price? Type the exact amount you have in mind.",
      inputType: 'currency',
      placeholder: 'e.g. 450,000',
      parse: parseCurrency
    }, {
      field: 'propertyState',
      ask: 'Which state are you looking to buy in?',
      inputType: 'select',
      selectOptions: US_STATES,
      placeholder: 'Select a state'
    }, {
      field: 'downPayment',
      ask: 'How much are you planning to put down? Type the exact dollar amount.',
      inputType: 'currency',
      placeholder: 'e.g. 90,000',
      parse: parseCurrency
    }, {
      field: 'loanTerm',
      ask: 'What loan term feels right?',
      quickReplies: ['30 years', '20 years', '15 years', 'Not sure'],
      parse: r => {
        if (r.includes('30')) return 30;
        if (r.includes('20')) return 20;
        if (r.includes('15')) return 15;
        return 30;
      }
    }, {
      field: 'underContract',
      ask: 'Are you currently under contract?',
      quickReplies: ['Yes', 'No', 'In negotiation']
    }, {
      field: 'timeline',
      ask: 'When are you hoping to buy?',
      quickReplies: ['ASAP', 'Within 3 months', '3–6 months', '6+ months', 'Just exploring']
    }, {
      field: 'agent',
      ask: 'Working with a real estate agent yet?',
      quickReplies: ['Yes', 'Not yet', 'I am one']
    }, {
      ...NAME_STEP,
      ask: "Almost done! What's your full name so I can personalize your results?"
    }, EMAIL_STEP, PHONE_STEP];
  }
  if (intent === 'refinance') {
    return [CURRENT_RATE_STEP, TARGET_RATE_STEP, {
      field: 'goal',
      ask: "What's your main goal with refinancing?",
      quickReplies: ['Lower my payment', 'Lower my rate', 'Shorten my term', 'Cash out equity']
    }, {
      field: 'propertyState',
      ask: 'Which state is the property located in?',
      inputType: 'select',
      selectOptions: US_STATES,
      placeholder: 'Select a state'
    }, {
      field: 'homeValue',
      ask: "What's your home worth today, roughly?",
      inputType: 'currency',
      placeholder: 'e.g. 500,000',
      parse: parseCurrency
    }, {
      field: 'currentBalance',
      ask: 'And about how much do you still owe on your current mortgage?',
      inputType: 'currency',
      placeholder: 'e.g. 320,000',
      parse: parseCurrency
    }, {
      field: 'loanTerm',
      ask: 'What loan term are you considering for the new loan?',
      quickReplies: ['30 years', '20 years', '15 years', 'Not sure'],
      parse: r => {
        if (r.includes('30')) return 30;
        if (r.includes('20')) return 20;
        if (r.includes('15')) return 15;
        return 30;
      }
    }, NAME_STEP, EMAIL_STEP, PHONE_STEP];
  }
  // Equity / HELOC — no rate questions; just enough to show accessible equity.
  return [{
    field: 'homeValue',
    ask: "Nice. What's your home worth today, roughly?",
    inputType: 'currency',
    placeholder: 'e.g. 500,000',
    parse: parseCurrency
  }, {
    field: 'propertyState',
    ask: 'Which state is the property located in?',
    inputType: 'select',
    selectOptions: US_STATES,
    placeholder: 'Select a state'
  }, {
    field: 'currentBalance',
    ask: 'About how much do you still owe on your mortgage?',
    inputType: 'currency',
    placeholder: 'e.g. 250,000',
    parse: parseCurrency
  }, {
    field: 'cashOutAmount',
    ask: 'How much cash are you hoping to pull out? Type the exact dollar amount.',
    inputType: 'currency',
    placeholder: 'e.g. 50,000',
    parse: parseCurrency
  }, {
    field: 'goal',
    ask: 'What are you planning to use it for?',
    quickReplies: ['Home renovation', 'Debt consolidation', 'Investment', 'Major purchase', 'Something else']
  }, {
    field: 'timeline',
    ask: 'When are you hoping to tap into your equity?',
    quickReplies: ['ASAP', 'Within 3 months', '3–6 months', 'Just exploring']
  }, NAME_STEP, EMAIL_STEP, PHONE_STEP];
}
export function ChatPanel({
  intent,
  scenario,
  onUpdate
}: ChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [stepIdx, setStepIdx] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const steps = useRef(getSteps(intent).filter(s => {
    if (!s.field) return true;
    const v = (scenario as any)[s.field];
    return v == null || v === '';
  })).current;
  const initStarted = useRef(false);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  useEffect(() => {
    if (initStarted.current) return;
    initStarted.current = true;
    const greeting = intent === 'purchase' ? "Hey! I'm Pronto 👋 I'll help you figure out what you can comfortably afford. Just a few quick questions — no forms, no SSN, no credit check. Let's go!" : intent === 'refinance' ? "Hey! I'm Pronto 👋 Let's see if refinancing makes sense for you. A few quick questions — no forms, no SSN. Let's go!" : "Hey! I'm Pronto 👋 Let's see how much cash your home can unlock. A few quick questions — no SSN, no hard credit pull. Let's go!";
    setIsTyping(true);
    const t1 = setTimeout(() => {
      setMessages([{
        id: 'g1',
        type: 'bot',
        text: greeting
      }]);
      setIsTyping(false);
      const t2 = setTimeout(() => askStep(0), 600);
      return () => clearTimeout(t2);
    }, 800);
    return () => clearTimeout(t1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);
  const askStep = (idx: number) => {
    const step = steps[idx];
    if (!step) {
      setIsTyping(true);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: 'done',
          type: 'bot',
          text: "Perfect — got everything I need! 🎉 I'm putting together your personalized results. A Loan Pronto advisor will reach out shortly with options that fit your scenario."
        }]);
        setIsTyping(false);
      }, 1000);
      return;
    }
    let askText = step.ask;
    if (step.field === 'email') {
      askText = `Thanks${scenario.name ? `, ${scenario.name.split(' ')[0]}` : ''}! What's the best email to send your personalized report to?`;
    }
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `b-${idx}`,
        type: 'bot',
        text: askText,
        quickReplies: step.quickReplies,
        inputType: step.inputType,
        placeholder: step.placeholder,
        selectOptions: step.selectOptions
      }]);
      setIsTyping(false);
    }, 1100);
  };
  const handleAnswer = (raw: string) => {
    if (!raw.trim()) return;
    const step = steps[stepIdx];
    if (!step) return;
    setMessages(prev => prev.map(m => ({
      ...m,
      quickReplies: undefined,
      selectOptions: undefined
    })).concat({
      id: `u-${stepIdx}`,
      type: 'user',
      text: raw
    }));
    setInputValue('');
    setSelectValue('');
    if (step.field) {
      let value: any = raw;
      if (step.parse) value = step.parse(raw);
      if (step.field === 'downPayment' && typeof value === 'number' && value <= 1) {
        const budget = scenario.budget ?? 0;
        value = Math.round(budget * value);
      }
      // Allow `parse` to return undefined as a signal to skip this field
      // (e.g. the user picks "No thanks, email is fine" on the phone step).
      if (value !== undefined) {
        onUpdate({
          [step.field]: value
        } as Partial<Scenario>);
      }
    }
    const next = stepIdx + 1;
    setStepIdx(next);
    askStep(next);
  };
  const currentStep = steps[stepIdx];
  const placeholder = currentStep?.placeholder || 'Type a message...';
  const isSelectStep = currentStep?.inputType === 'select';
  const inputType = currentStep?.inputType === 'email' ? 'email' : 'text';
  return <div className="bg-white rounded-3xl shadow-sm border border-forest/10 flex flex-col overflow-hidden h-[640px]" data-id="element-25">
      {/* Header */}
      <div className="bg-white border-b border-forest/5 p-4 flex items-center justify-between shrink-0" data-id="element-26">
        <div className="flex items-center gap-3" data-id="element-27">
          <div className="relative" data-id="element-28">
            <div className="w-10 h-10 bg-lime rounded-full flex items-center justify-center shadow-sm" data-id="element-29">
              <Sparkles className="w-5 h-5 text-forest" strokeWidth={2.5} data-id="element-30" />
            </div>
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-lime-500 border-2 border-white rounded-full" data-id="element-31" />
          </div>
          <div data-id="element-32">
            <h3 className="font-extrabold text-forest leading-tight" data-id="element-33">Pronto</h3>
            <p className="text-xs text-forest/60" data-id="element-34">AI Mortgage Assistant</p>
          </div>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-cream hover:bg-cream-deep text-forest text-xs font-bold rounded-full transition-colors group relative" data-id="element-35">
          <Phone className="w-3.5 h-3.5" data-id="element-36" />
          <span data-id="element-37">Talk instead</span>
          <div className="absolute -bottom-8 right-0 bg-forest text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-38">
            Coming soon
          </div>
        </button>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5 bg-cream/40" data-id="element-39">
        {messages.map(msg => <motion.div key={msg.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={cn('flex flex-col max-w-[85%]', msg.type === 'user' ? 'ml-auto items-end' : 'mr-auto items-start')} data-id="element-40">
            <div className={cn('px-4 py-3 rounded-2xl text-[15px] leading-relaxed shadow-sm', msg.type === 'user' ? 'bg-forest text-white rounded-br-sm' : 'bg-white border border-forest/5 text-forest rounded-bl-sm')} data-id="element-41">
              {msg.text}
            </div>

            {msg.quickReplies && <div className="flex flex-wrap gap-2 mt-3" data-id="element-42">
                {msg.quickReplies.map((reply, idx) => <button key={idx} onClick={() => handleAnswer(reply)} className="px-4 py-2 bg-white border-2 border-lime-300 text-forest text-sm font-semibold rounded-full hover:bg-lime-50 hover:border-lime-500 transition-colors shadow-sm" data-id="element-43">
                    {reply}
                  </button>)}
              </div>}
          </motion.div>)}

        {isTyping && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-start max-w-[85%] mr-auto" data-id="element-44">
            <div className="px-4 py-3.5 bg-white border border-forest/5 rounded-2xl rounded-bl-sm shadow-sm flex items-center gap-1" data-id="element-45">
              <div className="w-1.5 h-1.5 bg-forest/40 rounded-full animate-bounce" style={{
            animationDelay: '0ms'
          }} data-id="element-46" />
              <div className="w-1.5 h-1.5 bg-forest/40 rounded-full animate-bounce" style={{
            animationDelay: '150ms'
          }} data-id="element-47" />
              <div className="w-1.5 h-1.5 bg-forest/40 rounded-full animate-bounce" style={{
            animationDelay: '300ms'
          }} data-id="element-48" />
            </div>
          </motion.div>}
        <div ref={messagesEndRef} data-id="element-49" />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-white border-t border-forest/5 shrink-0" data-id="element-50">
        {isSelectStep ? <form onSubmit={e => {
        e.preventDefault();
        if (selectValue) handleAnswer(selectValue);
      }} className="relative flex items-center gap-2" data-id="element-51">
            <div className="relative flex-1" data-id="element-52">
              <select value={selectValue} onChange={e => setSelectValue(e.target.value)} disabled={isTyping || !currentStep} className="w-full appearance-none pl-4 pr-10 py-3.5 bg-cream/60 border border-forest/10 rounded-xl text-[15px] text-forest focus:outline-none focus:ring-2 focus:ring-lime-400 focus:border-lime-400 transition-all disabled:opacity-60 cursor-pointer" data-id="element-53">
                <option value="" disabled data-id="element-54">
                  {placeholder}
                </option>
                {currentStep?.selectOptions?.map(opt => <option key={opt} value={opt} data-id="element-55">
                    {opt}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-forest/50 pointer-events-none" data-id="element-56" />
            </div>
            <button type="submit" disabled={!selectValue || isTyping || !currentStep} className="px-4 py-3.5 bg-lime hover:bg-lime-500 text-forest font-bold text-sm rounded-xl disabled:opacity-50 disabled:hover:bg-lime transition-colors flex items-center gap-1.5" data-id="element-57">
              <Send className="w-4 h-4" data-id="element-58" />
            </button>
          </form> : <form onSubmit={e => {
        e.preventDefault();
        handleAnswer(inputValue);
      }} className="relative flex items-center" data-id="element-59">
            <input type={inputType} value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={placeholder} disabled={isTyping || !currentStep} className="w-full pl-4 pr-12 py-3.5 bg-cream/60 border border-forest/10 rounded-xl text-[15px] text-forest focus:outline-none focus:ring-2 focus:ring-lime-400 focus:border-lime-400 transition-all placeholder:text-forest/40 disabled:opacity-60" data-id="element-60" />
            <button type="submit" disabled={!inputValue.trim() || isTyping || !currentStep} className="absolute right-2 p-2 text-lime-600 hover:bg-lime-50 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent transition-colors" data-id="element-61">
              <Send className="w-5 h-5" data-id="element-62" />
            </button>
          </form>}
      </div>
    </div>;
}